// src/components/public/RegistrationOverlay.js
// Overlay tipo Instagram que cubre el dashboard cuando un anonimo entra.
// NO se puede cerrar: la unica salida es registrarse o iniciar sesion.
//
// Visualmente: backdrop oscuro semi-transparente sobre TODO el viewport,
// modal centrado con CTAs duales (profesional / empresa) y link a login.
// Para que el dashboard real (BronzeMemberDashboard) se vea por detras —
// sensacion clasica "feed bloqueado" de Instagram.

import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiUser, FiBriefcase, FiZap, FiLock } from 'react-icons/fi';

const RegistrationOverlay = () => {
  const location = useLocation();
  const redirect = location.pathname + (location.search || '');

  // Bloquear scroll del body mientras el overlay esta visible
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const registerHref = (role) =>
    `/register?role=${role}&from=preview&redirect=${encodeURIComponent(redirect)}`;
  const loginHref = `/login?redirect=${encodeURIComponent(redirect)}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="registration-overlay-title"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
    >
      {/* Backdrop: dejamos ver el dashboard por detras pero bloquea interaccion */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Modal — no se puede cerrar haciendo click afuera */}
      <div className="relative w-full max-w-md bg-dark-800 border border-dark-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Banda superior con gradiente */}
        <div className="h-1.5 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600" />

        <div className="p-6 sm:p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-600/20 text-primary-400 mb-4">
            <FiLock size={26} />
          </div>

          <h2 id="registration-overlay-title" className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Sumate a AVLink Network
          </h2>
          <p className="text-gray-300 text-sm sm:text-base mb-6">
            Para ver el feed completo, los empleos y la comunidad audiovisual mas grande de Latinoamerica, crea tu cuenta gratis en menos de 1 minuto.
          </p>

          {/* Beneficios concisos */}
          <ul className="text-left text-sm text-gray-300 space-y-1.5 mb-6 max-w-xs mx-auto">
            <li className="flex items-start">
              <FiZap className="text-primary-400 mt-0.5 mr-2 flex-shrink-0" size={14} />
              <span>Feed completo de la comunidad AV</span>
            </li>
            <li className="flex items-start">
              <FiZap className="text-primary-400 mt-0.5 mr-2 flex-shrink-0" size={14} />
              <span>Acceso a empleos del sector</span>
            </li>
            <li className="flex items-start">
              <FiZap className="text-primary-400 mt-0.5 mr-2 flex-shrink-0" size={14} />
              <span>Cursos, eventos y networking</span>
            </li>
          </ul>

          {/* CTAs duales */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <Link
              to={registerHref('individual')}
              className="flex flex-col items-center justify-center px-4 py-4 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-semibold transition-colors shadow-lg"
            >
              <FiUser size={20} className="mb-1" />
              <span className="text-sm">Soy profesional</span>
              <span className="text-xs text-primary-100/80 font-normal mt-0.5">
                Tecnico, ingeniero, freelance
              </span>
            </Link>
            <Link
              to={registerHref('company')}
              className="flex flex-col items-center justify-center px-4 py-4 bg-dark-700 hover:bg-dark-600 text-white rounded-xl font-semibold transition-colors border border-dark-600"
            >
              <FiBriefcase size={20} className="mb-1" />
              <span className="text-sm">Soy empresa</span>
              <span className="text-xs text-gray-400 font-normal mt-0.5">
                Productora, integrador, marca
              </span>
            </Link>
          </div>

          <div className="pt-3 border-t border-dark-700">
            <p className="text-xs text-gray-400">
              Ya tenes cuenta?{' '}
              <Link to={loginHref} className="text-primary-400 hover:text-primary-300 font-semibold">
                Inicia sesion
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationOverlay;
